import { useGetInvoices } from "@entities/accounting/lib/hooks/useGetInvoices";
import { useGetBrands } from "@entities/brand";
import { ToastService } from "@shared/lib/services";
import { IBrandAccounting } from "@shared/lib/types/accounting";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Steps,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table";
import classNames from "classnames";
import dayjs from "dayjs";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileText,
  Info,
  Search,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { AccountingPaymentStatus } from "../lib/constants";
import { useCreatePayment } from "../lib/hooks/useCreatePayment";
import styles from "./AddPaymentModal.module.scss";

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [step, setStep] = useState(1);
  const [selectedBrand, setSelectedBrand] = useState<string | undefined>();
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [invoiceAllocations, setInvoiceAllocations] = useState<
    Record<
      string,
      {
        allocation: number;
        tdsAmount?: number;
      }
    >
  >({});
  const [paymentType, setPaymentType] = useState<string>("Received");
  const [amount, setAmount] = useState<number | "">("");
  const paymentDate = dayjs(new Date().toISOString());
  const [paymentMethod, setPaymentMethod] = useState("Bank-Transfer");

  const [brandSearch, setBrandSearch] = useState("");
  const [invoiceSearch, setInvoiceSearch] = useState("");

  const [form] = Form.useForm();

  const { brands, isFetching: areBrandsLoading } = useGetBrands(
    10,
    1,
    brandSearch
  );
  const { invoices, isFetching: areInvoicesLoading } = useGetInvoices({
    brandIds: selectedBrand ? [selectedBrand] : undefined,
    searchQuery: invoiceSearch,
    paymentStatuses: [
      AccountingPaymentStatus.partiallyPaid,
      AccountingPaymentStatus.pending,
    ],
  });
  const { createPayment, isCreating, isCreated, createPaymentError } =
    useCreatePayment();

  const brandOptions = useMemo(
    () => brands?.data?.map((b) => ({ label: b.name, value: b._id })) ?? [],
    [brands]
  );

  const filteredInvoices = invoices;

  const totalSelectedInvoiceAmount =
    filteredInvoices
      ?.filter((inv) => selectedInvoices.includes(inv._id))
      .reduce((sum, inv) => sum + inv.totalAmount, 0) ?? 0;

  const totalAllocated = Object.values(invoiceAllocations).reduce(
    (sum, val) => sum + val.allocation,
    0
  );

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Payment added successfully");
      onClose();
    } else if (createPaymentError) {
      ToastService.error(createPaymentError.message);
    }
  }, [isCreated, createPaymentError]);

  useEffect(() => {
    setSelectedInvoices([]);
    setInvoiceAllocations({});
    setAmount("");
  }, [selectedBrand]);

  const toggleInvoice = (id: string) => {
    let newSelected: string[];
    if (selectedInvoices.includes(id)) {
      newSelected = selectedInvoices.filter((i) => i !== id);
    } else {
      newSelected = [...selectedInvoices, id];
    }
    setSelectedInvoices(newSelected);

    const newAllocations: Record<
      string,
      {
        allocation: number;
        tdsAmount?: number;
      }
    > = {};
    newSelected.forEach((invId) => {
      const invoice = invoices?.find((i) => i._id === invId);
      newAllocations[invId] = {
        allocation:
          invoiceAllocations[invId]?.allocation ||
          (invoice?.totalAmount && invoice.paymentAmountReceived)
            ? Math.abs(invoice?.totalAmount! - invoice?.paymentAmountReceived!)
            : invoice?.totalAmount || 0,
      };
    });
    setInvoiceAllocations(newAllocations);
  };

  const handleAllocationChange = (
    id: string,
    value: number,
    tdsAmount?: number
  ) => {
    setInvoiceAllocations({
      ...invoiceAllocations,
      [id]: {
        allocation: value,
        tdsAmount: tdsAmount,
      },
    });
  };

  const handleNext = () => {
    if (step === 1 && selectedBrand) setStep(2);
    else if (step === 2 && selectedInvoices.length > 0) {
      // if (selectedInvoices.length > 1) setStep(3);
      // else {
      //   setAmount(invoiceAllocations[selectedInvoices[0]].allocation || 0);
      //   setStep(4);
      // }
      setStep(3);
    } else if (step === 3) {
      setAmount(totalAllocated);
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      onClose();
      return;
    } else if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else if (step === 4) {
      if (selectedInvoices.length > 0) setStep(3);
      else setStep(2);
    }
  };

  const handleSubmit = (values: any) => {
    if (!selectedBrand) return;
    createPayment({
      brandId: selectedBrand,
      subType: paymentType,
      amountBreakDown: Object.keys(invoiceAllocations).map((val) => ({
        brandAccountingId: val,
        amount: invoiceAllocations[val].allocation,
        tdsAmount: invoiceAllocations[val].tdsAmount,
        isTdsDeducted: invoiceAllocations[val].tdsAmount !== undefined,
      })),
      tnxReference: values.reference,
      method: values.paymentMethod,
    });
  };

  const invoiceColumns: ColumnsType<IBrandAccounting> = [
    {
      title: "",
      dataIndex: "_id",
      key: "select",
      width: 50,
      render: (id: string) => (
        <div
          className={classNames(
            styles.selectCell,
            styles.selectCellSquare,
            selectedInvoices.includes(id) ? styles.selected : ""
          )}
        >
          {selectedInvoices.includes(id) && <Check size={12} />}
        </div>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "accountingNumber",
      key: "invoice",
      render: (text: string, record) => (
        <div>
          <div className={styles.invoiceText}>{text}</div>
          <div className={styles.invoiceDate}>
            {new Date(record.date).toLocaleDateString()}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "status",
      render: (status: string) => (
        <Tag color="blue" style={{ textTransform: "uppercase" }}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, record) => (
        <div className={styles.invoiceAmount}>
          ₹{record.totalAmount.toString()}
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      centered
      footer={
        !isCreated && (
          <div className={styles.modalFooter}>
            <Button onClick={handleBack} type="text">
              {step === 1 ? "Cancel" : "Back"}
            </Button>
            {step < 4 ? (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={
                  (step === 1 && !selectedBrand) ||
                  (step === 2 && selectedInvoices.length === 0) ||
                  (step === 3 && totalAllocated <= 0)
                }
              >
                Next <ChevronRight size={16} />
              </Button>
            ) : (
              <Button
                type="primary"
                htmlType="submit"
                form="add-payment-form"
                loading={isCreating}
              >
                Save Entry
              </Button>
            )}
          </div>
        )
      }
      width={650}
      className={styles.addPaymentModal}
      title={
        <div className={styles.titleContainer}>
          <div className={styles.iconWrapper}>
            <CreditCard size={18} />
          </div>
          <div className={styles.titleText}>
            <h2>Payment / Refund</h2>
            <p>Link a payment or refund to selected invoices</p>
          </div>
        </div>
      }
      styles={{
        header: {
          marginBottom: 0,
          padding: 16,
        },
        footer: {
          marginTop: 0,
        },
      }}
    >
      <div className={styles.modalBody}>
        {isCreated ? (
          <div className={styles.successScreen}>
            <div className={styles.successIcon}>
              <CheckCircle2 size={32} />
            </div>
            <h3 className={styles.successTitle}>Entry Saved</h3>
            <p className={styles.successDescription}>
              The payment or refund has been successfully recorded and linked to
              the invoices.
            </p>
          </div>
        ) : (
          <div className={styles.stepsContainer}>
            <Steps
              current={step - 1}
              className={styles.stepIndicator}
              items={[
                { title: "Brand" },
                { title: "Invoices" },
                // ...(selectedInvoices.length > 0
                //   ? [{ title: "Proportions" }]
                //   : []),
                { title: "Proportions" },
                { title: "Details" },
              ]}
            />

            {step === 1 && (
              <div className={styles.stepContent}>
                <Input
                  prefix={<Search size={16} />}
                  placeholder="Search brand..."
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  className={styles.searchInput}
                />
                <div className={styles.brandTable}>
                  <Table
                    dataSource={brandOptions}
                    columns={[
                      {
                        title: "Brand Name",
                        dataIndex: "label",
                        render: (label: string, record: { value: string }) => (
                          <div className={styles.brandCell}>
                            <div className={styles.brandIcon}>
                              <Building2 size={16} />
                            </div>
                            <span className={styles.brandName}>{label}</span>
                          </div>
                        ),
                      },
                      {
                        title: "Action",
                        dataIndex: "value",
                        align: "right",
                        render: (value: string) => (
                          <div
                            className={classNames(
                              styles.selectCell,
                              selectedBrand === value ? styles.selected : ""
                            )}
                          >
                            {selectedBrand === value && <Check size={12} />}
                          </div>
                        ),
                      },
                    ]}
                    onRow={(record) => ({
                      onClick: () => {
                        setSelectedBrand(record.value);
                        setSelectedInvoices([]);
                        setInvoiceAllocations({});
                      },
                    })}
                    rowClassName={(record) =>
                      `${styles.brandRow} ${
                        selectedBrand === record.value ? styles.selected : ""
                      }`
                    }
                    pagination={false}
                    loading={areBrandsLoading}
                    scroll={{
                      y: 300,
                    }}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className={styles.stepContent}>
                <Input
                  prefix={<Search size={16} />}
                  placeholder="Search invoices by ID or status..."
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  className={styles.searchInput}
                />
                <div className={styles.invoiceTable}>
                  <Table
                    dataSource={filteredInvoices}
                    columns={invoiceColumns}
                    rowKey="_id"
                    pagination={false}
                    loading={areInvoicesLoading}
                    onRow={(record) => ({
                      onClick: () => toggleInvoice(record._id),
                      style: {
                        cursor: "pointer",
                      },
                    })}
                    scroll={{
                      y: 300,
                    }}
                  />
                </div>
                {selectedInvoices.length > 0 && (
                  <div className={styles.totalSelected}>
                    <span className={styles.totalSelectedLabel}>
                      Total Selected:
                    </span>
                    <span className={styles.totalSelectedAmount}>
                      ₹{totalSelectedInvoiceAmount.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className={styles.stepContent}>
                <div className={styles.infoBox}>
                  <FileText size={18} className={styles.icon} />
                  <div>
                    <h4 className={styles.infoBoxTitle}>
                      Map Payment Proportions
                    </h4>
                    <p className={styles.infoBoxDescription}>
                      Allocate how much of the total payment belongs to each
                      selected invoice.
                    </p>
                  </div>
                </div>
                <div className={styles.proportionsTable}>
                  <Table
                    dataSource={selectedInvoices.map((id) =>
                      invoices?.find((i) => i._id === id)
                    )}
                    columns={[
                      {
                        title: "Invoice",
                        dataIndex: "accountingNumber",
                      },
                      {
                        title: "Full Amount",
                        // dataIndex: "totalAmount",
                        align: "right",
                        render: (_, record) =>
                          record?.paymentAmountReceived
                            ? `₹${(record?.totalAmount! - record?.paymentAmountReceived!).toLocaleString()}`
                            : `₹${record?.totalAmount.toLocaleString()}`,
                      },
                      {
                        title: "Allocation",
                        dataIndex: "_id",
                        align: "right",
                        width: 150,
                        render: (id) => (
                          <InputNumber
                            prefix="₹"
                            value={invoiceAllocations[id].allocation || ""}
                            onChange={(value) =>
                              handleAllocationChange(
                                id,
                                value as number,
                                invoiceAllocations[id].tdsAmount
                              )
                            }
                            style={{ width: "100%" }}
                          />
                        ),
                      },
                      {
                        title: "TDS Amount (Optional)",
                        dataIndex: "_id",
                        align: "right",
                        width: 150,

                        render: (id) => (
                          <InputNumber
                            prefix="₹"
                            value={invoiceAllocations[id]?.tdsAmount || ""}
                            onChange={(value) =>
                              handleAllocationChange(
                                id,
                                invoiceAllocations[id].allocation,
                                value as number
                              )
                            }
                            style={{ width: "100%" }}
                          />
                        ),
                      },
                    ]}
                    pagination={false}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2} align="right">
                          <b style={{ fontSize: 12 }}>Total Allocated:</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <b style={{ fontSize: 12, color: "#635bff" }}>
                            ₹{totalAllocated.toLocaleString()}
                          </b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </div>
              </div>
            )}

            {step === 4 && (
              <Form
                form={form}
                name="add-payment-form"
                layout="vertical"
                onFinish={handleSubmit}
                className={styles.stepContent}
                requiredMark={false}
              >
                <Row gutter={[10, 15]}>
                  <Col span={24}>
                    <Form.Item
                      label="Entry Type"
                      name="entryType"
                      initialValue={"Received"}
                      rules={[
                        {
                          required: true,
                          message: "Please select entry type.",
                        },
                      ]}
                    >
                      <Radio.Group
                        onChange={(e) => setPaymentType(e.target.value)}
                        value={paymentType}
                        className={styles.radioGroup}
                      >
                        <Radio value="Received" className={styles.payment}>
                          <span className={styles.radioLabel}>
                            <ArrowDownLeft size={16} /> Payment
                          </span>
                        </Radio>
                        <Radio value="Refund" className={styles.refund}>
                          <span className={styles.radioLabel}>
                            <ArrowUpRight size={16} /> Refund
                          </span>
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Amount"
                      name="amount"
                      rules={[
                        {
                          required: true,
                          message: "Please enter amount.",
                        },
                      ]}
                      initialValue={amount}
                    >
                      <InputNumber prefix="₹" style={{ width: "100%" }} />
                    </Form.Item>
                    <Button type="link" size="small">
                      Use Allocated Total
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Date"
                      name="paymentDate"
                      initialValue={paymentDate}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  {Object.keys(invoiceAllocations)?.filter(
                    (val) => invoiceAllocations[val].tdsAmount!
                  )?.length > 0 ? (
                    <Col span={24}>
                      <Card
                        className={styles.infoBox}
                        styles={{
                          body: {
                            padding: 0,
                          },
                        }}
                      >
                        <Flex gap={10}>
                          <Info size={11} className={styles.icon} />
                          <Typography.Text
                            style={{
                              margin: 0,
                              color: "#0369a1",
                              fontSize: "11px",
                            }}
                          >
                            Optional TDS amount deducted from this {paymentType}
                            . An additional entry will be created for the TDS
                            deduction.
                          </Typography.Text>
                        </Flex>
                      </Card>
                    </Col>
                  ) : null}
                  <Col span={12}>
                    {/* <Form.Item label="TDS Amount (Optional)" name="tdsAmount">
                      <InputNumber prefix="₹" style={{ width: "100%" }} />
                    </Form.Item> */}
                  </Col>
                  <Col span={12}></Col>
                  <Col span={12}>
                    <Form.Item
                      label="Method"
                      name="paymentMethod"
                      initialValue={paymentMethod}
                      rules={[
                        {
                          required: true,
                          message: "Please select a payment method.",
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="Bank-Transfer">
                          Bank Transfer
                        </Select.Option>
                        <Select.Option value="UPI">UPI</Select.Option>
                        <Select.Option value="Cheque">Cheque</Select.Option>
                        <Select.Option value="Cash">Cash</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Reference / Txn ID"
                      name="reference"
                      rules={[
                        {
                          required: true,
                          message: "Please enter reference or Txn ID.",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
